import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { CloseSmallIcon } from '@/assets/icons/CloseSmallIcon';
import { SheetScreen } from '@/components/ScreenComponent';
import { SCREEN_NAV_BAR_HEIGHT } from '@/components/ScreenNavBar';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { common } from '@/styles/styles';

const FADE_DURATION = 300;

type Props = {
    onClose: () => void;
    children: ReactNode;
    /** When true, fades and scales content in/out based on `visible`. */
    animated?: boolean;
    visible?: boolean;
    onExitComplete?: () => void;
};

export const EntryConfirmationShell = ({ onClose, children, animated, visible, onExitComplete }: Props) => {
    const progress = useSharedValue(animated ? 0 : 1);
    const hasBeenVisible = useRef(false);
    const onExitCompleteRef = useRef(onExitComplete);
    onExitCompleteRef.current = onExitComplete;

    const callOnExitComplete = useCallback(() => {
        if (hasBeenVisible.current) onExitCompleteRef.current?.();
    }, []);

    useEffect(() => {
        if (!animated) return;
        if (visible) {
            hasBeenVisible.current = true;
            progress.value = withTiming(1, { duration: FADE_DURATION });
        } else {
            progress.value = withTiming(0, { duration: FADE_DURATION }, finished => {
                if (finished) runOnJS(callOnExitComplete)();
            });
        }
    }, [animated, visible, callOnExitComplete, progress]);

    const animatedStyle = useAnimatedStyle(() => ({
        flex: 1,
        opacity: progress.value,
    }));

    return (
        <SheetScreen edges={['top', 'bottom']} style={[common.flex, styles.screen]}>
            <Animated.View style={animatedStyle}>
                {children}
                {/*
                 * Absolute positioned so that the content can be centered vertically using the entire screen's height
                 */}
                <Box position={'absolute'} top={0} left={0} right={0} pointerEvents="box-none">
                    <Box
                        flexDirection="row"
                        alignItems="center"
                        paddingHorizontal="s24"
                        marginTop="s10"
                        height={SCREEN_NAV_BAR_HEIGHT}
                    >
                        <TouchableOpacity
                            onPress={onClose}
                            hitSlop={12}
                            style={styles.closeButton}
                            testID="entryConfirmationClose"
                        >
                            <CloseSmallIcon />
                        </TouchableOpacity>
                    </Box>
                </Box>
            </Animated.View>
        </SheetScreen>
    );
};

const styles = StyleSheet.create({
    screen: {
        backgroundColor: 'transparent',
    },
    closeButton: {
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: ds.colorValue.background.transparent.subtle,
        borderWidth: 1,
        borderColor: ds.colorValue.border.strong,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
